'use client'

import { useEffect } from 'react'
import { useIntl } from 'react-intl'
import { Cell, Spacer, Text } from '@vinted/web-ui'

import { useTracking } from '@marketplace-web/shared/event-tracker'
import { useTranslate } from '@marketplace-web/shared/i18n'
import { viewScreenEvent } from '_libs/common/event-tracker/events'
import { formatCurrency } from '@marketplace-web/domain/currency-data'
import {
  ConversationEducationModel,
  EducationType,
} from '@marketplace-web/conversation-education/conversation-education-data'
import { Screen } from 'constants/tracking/screens'

import ItemPhoto from './ItemPhoto'
import Steps from './Steps'
import ElectronicsSteps from './ElectronicsSteps'

type Props = {
  type: EducationType
  showInWebview?: boolean
  conversation?: ConversationEducationModel
}

const ConversationEducationContent = ({ type, showInWebview, conversation }: Props) => {
  const translate = useTranslate('conversation.education')
  const { track } = useTracking()

  const { locale } = useIntl()

  const itemsCount =
    ((conversation?.transaction?.itemsCount || 0) > 1 && conversation?.transaction?.itemsCount) ||
    undefined

  const itemName = conversation?.transaction?.order.title

  const isBundle = conversation?.transaction?.itemsCount && conversation.transaction.itemsCount > 1

  const price =
    conversation?.item?.priceNumeric && conversation.item.currency
      ? formatCurrency(conversation.item.priceNumeric, {
          locale,
          currency: conversation.item.currency,
        })
      : null

  const isTransactionCompleted = [
    EducationType.InConversationTransactionCompleted,
    EducationType.InConversationTransactionCompletedHAOV,
    EducationType.InConversationTransactionCompletedElectronics,
    EducationType.InConversationTransactionCompletedElectronicsEvs,
  ].includes(type)

  const isTransactionElectronics = [
    EducationType.InConversationSoldElectronics,
    EducationType.InConversationSoldElectronicsEvs,
    EducationType.InConversationTransactionCompletedElectronics,
    EducationType.InConversationTransactionCompletedElectronicsEvs,
  ].includes(type)

  useEffect(() => {
    if (isTransactionElectronics) {
      const isEvs = [
        EducationType.InConversationSoldElectronicsEvs,
        EducationType.InConversationTransactionCompletedElectronicsEvs,
      ].includes(type)

      track(
        viewScreenEvent({
          screen: isEvs
            ? Screen.ElectronicsEvsFirstSaleEducation
            : Screen.ElectronicsFirstSaleEducation,
        }),
      )

      return
    }

    const isTransactionHAOV = [
      EducationType.InConversationTransactionCompletedHAOV,
      EducationType.InConversationSoldHAOV,
    ].includes(type)

    const currentScreen = isTransactionHAOV
      ? Screen.ConversationHAOVEducation
      : Screen.ConversationEducation

    track(viewScreenEvent({ screen: currentScreen }))
  }, [type, track, isTransactionElectronics])

  const getDescription = () => {
    if (isTransactionCompleted) return translate('description.transaction_completed', { price })

    if (isBundle) return translate('description.bundle_sold', { price })

    return translate('description.item_sold', { price, item_name: itemName })
  }

  const getPhotoText = () =>
    isTransactionCompleted ? translate('item_photo.done') : translate('item_photo.sold')

  const renderDescription = () => (
    <Cell theme="transparent" styling={Cell.Styling.Wide}>
      <Text
        as="span"
        text={getDescription()}
        alignment={Text.Alignment.Center}
        width={Text.Width.Parent}
      />
    </Cell>
  )

  const renderItemPhoto = () => (
    <div className="u-ui-padding-horizontal-large">
      <Spacer size={Spacer.Size.XLarge} />
      <ItemPhoto
        url={conversation?.item?.photo?.url}
        bottomText={getPhotoText()}
        itemsCount={itemsCount}
      />
    </div>
  )

  const renderHeader = () => {
    return (
      <div className="conversation-education__background u-flexbox u-position-relative u-flex-direction-column u-align-items-center">
        {showInWebview && <Spacer size={Spacer.Size.Large} />}
        <div className="u-flexbox u-align-items-center">{renderItemPhoto()}</div>
        {renderDescription()}
      </div>
    )
  }

  const renderSteps = () => {
    if (isTransactionElectronics) {
      return <ElectronicsSteps type={type} price={price} />
    }

    return <Steps type={type} price={price} />
  }

  if (!conversation) return null

  return (
    <div className="u-fill-width">
      {renderHeader()}
      <Spacer size={Spacer.Size.Regular} />
      {renderSteps()}
    </div>
  )
}

export default ConversationEducationContent
